import { useState, useEffect } from 'react'

/**
 * Custom hook for specific media query detection
 *
 * USE CASES:
 * - Accessibility preferences (prefers-contrast, prefers-colors-scheme)
 * - Hardware capabilities (hover, pointer precision)
 * - Device orientation
 *
 * @param {string} query - CSS media query string
 * @returns {boolean} Whether the media query matches
 * @example
 *
 * // ✅ Color scheme preference
 * const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')
 * // ✅ Touch device detection
 * const isTouch = useMediaQuery('(hover: none)')
 *
 * @note For prefers-reduced-motion, use useAnimations() context instead
 * @see useDeviceType For standard responsive breakpoints
 */

export function useMediaQuery (query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)

  useEffect(() => {
    const mediaQuery = window.matchMedia(query)
    const handler = () => setMatches(mediaQuery.matches)
    mediaQuery.addEventListener('change', handler)

    return () => mediaQuery.removeEventListener('change', handler)
  }, [query])

  return matches
}
